// Third Party
import React, { useRef, useCallback, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

// Actions
import { ReduxState } from '../../../../../redux/modules/types';
import { updateComparisonItemColor } from '../../../../../redux/modules/comparison';

// Components
import ComparisonScrollbarContainer from '../../misc/ComparisonScrollbarContainer';
import HistoricalScenariosTableLabels from './components/HistoricalScenariosTableLabels';
import HistoricalScenariosStreamCard from './components/HistoricalScenariosStreamCard/HistoricalScenariosStreamCard';

// Utils
import ComparisonItem from '@/types/comparison-item';
import Nullable from '@/types/nullable';
import User from '@/types/user';
import Comparison from '@/types/comparison';
import { filterHistoricalScenarioItems } from '@/utils/historical-scenarios';

type DispatchProps = {
  updateComparisonItemColor: (item: ComparisonItem, color: string) => void;
};

type StateProps = {
  items: ComparisonItem[];
  user?: Nullable<User>;
  isEditPinned: boolean;
  comparison: Comparison;
};

type Props = StateProps &
  DispatchProps & {
    popoverContainerRef: Nullable<HTMLDivElement>;
  };

const HistoricalScenariosTable: React.FC<Props> = ({ items, user, isEditPinned, comparison, updateComparisonItemColor, popoverContainerRef }) => {
  const historicalScenarioTableRef = useRef<HTMLDivElement>(null);
  const comparisonScrollbarContainerRef = useRef<ComparisonScrollbarContainer | null>(null);
  const [scrollContainerRef, setScrollContainerRef] = useState<HTMLElement | null | undefined>(undefined);
  const [tablePaddingRef, setTablePaddingRef] = useState<HTMLElement | null | undefined>(undefined);

  useEffect(() => {
    if (comparisonScrollbarContainerRef.current) {
      setScrollContainerRef(comparisonScrollbarContainerRef.current.getScrollContainerRef());
      setTablePaddingRef(comparisonScrollbarContainerRef.current.getPaddingRef());
    }
  }, [items, isEditPinned]);

  const onScrollX = useCallback((atStart?: boolean) => {
    if (!historicalScenarioTableRef.current) {
      return;
    }
    if (atStart) {
      historicalScenarioTableRef.current.classList.remove('is-scrolled-x');
    } else {
      historicalScenarioTableRef.current.classList.add('is-scrolled-x');
    }
  }, []);

  const renderCard = useCallback(
    (item: ComparisonItem) => (
      <HistoricalScenariosStreamCard
        key={item._id}
        item={item}
        user={user}
        isEditPinned={isEditPinned}
        comparison={comparison}
        updateComparisonItemColor={updateComparisonItemColor}
        popoverContainerRef={popoverContainerRef}
        scrollbarContainerRef={scrollContainerRef ?? undefined}
      />
    ),
    [user, isEditPinned, comparison, updateComparisonItemColor, popoverContainerRef, scrollContainerRef],
  );

  const tableClasses = classNames('historical-scenario-table d-flex');

  return (
    <div className={tableClasses} ref={historicalScenarioTableRef}>
      {scrollContainerRef && tablePaddingRef && (
        <HistoricalScenariosTableLabels items={items} scrollContainerRef={scrollContainerRef} tablePaddingRef={tablePaddingRef} isEditPinned={isEditPinned} />
      )}
      <ComparisonScrollbarContainer
        className="historical-scenario-metrics-cards"
        isEditPinned={isEditPinned}
        withPadding
        onScrollX={onScrollX}
        ref={comparisonScrollbarContainerRef}
      >
        {filterHistoricalScenarioItems(items).map(i => renderCard(i))}
      </ComparisonScrollbarContainer>
    </div>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  user: state.auth.user,
});

const mapDispatchToProps = {
  updateComparisonItemColor,
};

export default connect(mapStateToProps, mapDispatchToProps)(HistoricalScenariosTable);
